import { CommonFormGrid } from 'components/form/grid';
import { FormHeading } from 'components/form/heading';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { OptionDef } from 'interfaces/options';
import {
  RevenueFnName,
  RevenuePeriodType,
  RevenueRecognitionType,
} from 'lib/enums/revenue';
import { RevenueStructureDoc } from 'lib/interfaces/database/revenue-structure';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useSecuritiesStore from 'stores/securities';

export const RevenueStructureForm = () => {
  const { data: securities, load: loadSecurities } = useSecuritiesStore();

  useEffect(() => {
    loadSecurities();
  }, []);

  const { data, setData } = useContext(FormContext);

  const [defaultData] = useState<Partial<RevenueStructureDoc>>(data);

  const securityOptions = useMemo<OptionDef[]>(() => {
    return securities.map((o) => ({
      label: o.name,
      value: o.id,
      group: o._issuer?.name,
    }));
  }, [securities]);

  return (
    <EB>
      <CommonFormGrid columns="2">
        <FormHeading label="Revenue Structure" gridColumn="span 2" />

        <FormSelect
          gridColumn="span 2"
          label="Security"
          defaultValue={defaultData.securityID}
          options={securityOptions}
          onChange={(v) => {
            const payload: Partial<RevenueStructureDoc> = {
              securityID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          gridColumn="span 2"
          label="Effective Date"
          defaultValue={StringUtil.printDate(defaultData.effective)}
          onChange={(v) => {
            const payload: Partial<RevenueStructureDoc> = {
              effective: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          label="Rate"
          defaultValue={StringUtil.printPercent(defaultData.rate)}
          onNumericChange={(v) => {
            const payload: Partial<RevenueStructureDoc> = {
              rate: v / 100,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Recognized"
          defaultValue={defaultData.recognized}
          options={Object.values(RevenueRecognitionType).map((o) => ({
            label: o,
            value: o,
          }))}
          onChange={(v) => {
            const payload: Partial<RevenueStructureDoc> = {
              recognized: v as RevenueRecognitionType,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Period"
          defaultValue={defaultData.period}
          options={Object.values(RevenuePeriodType).map((o) => ({
            label: o,
            value: o,
          }))}
          onChange={(v) => {
            const payload: Partial<RevenueStructureDoc> = {
              period: v as RevenuePeriodType,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Function"
          defaultValue={defaultData.fnName}
          options={Object.values(RevenueFnName).map((o) => ({
            label: o,
            value: o,
          }))}
          onChange={(v) => {
            const payload: Partial<RevenueStructureDoc> = {
              fnName: v as RevenueFnName,
            };
            setData(payload);
          }}
          required
        />
      </CommonFormGrid>
    </EB>
  );
};
